import { createContext, useContext, useEffect, useState, ReactNode } from 'react';

export type ButterStyle = 'soft' | 'frosted' | 'vibrant';
export type ButterHue = 'lavender' | 'peach' | 'mint' | 'rose' | 'sky';

export interface ButterSettings {
  style: ButterStyle;
  hue: ButterHue;
  blur: number;
  accent: string;
}

interface ButterContextType {
  settings: ButterSettings;
  setStyle: (style: ButterStyle) => void;
  setHue: (hue: ButterHue) => void;
  setBlur: (blur: number) => void;
  setAccent: (accent: string) => void;
}

const STORAGE_KEY = 'mamal_butter';

export const BUTTER_DEFAULTS: ButterSettings = {
  style: 'vibrant',
  hue: 'lavender',
  blur: 48,
  accent: '#8b7ad1',
};

export const HUE_LABELS: Record<ButterHue, string> = {
  lavender: 'Lavendel',
  peach: 'Pfirsich',
  mint: 'Mint',
  rose: 'Rose',
  sky: 'Himmelblau',
};

export const STYLE_LABELS: Record<ButterStyle, string> = {
  soft: 'Weich',
  frosted: 'Mattiert',
  vibrant: 'Lebendig',
};

function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? { r: parseInt(result[1], 16), g: parseInt(result[2], 16), b: parseInt(result[3], 16) }
    : null;
}

function loadFromStorage(): ButterSettings {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return { ...BUTTER_DEFAULTS, ...JSON.parse(stored) };
    }
  } catch {
    // ignore
  }
  return { ...BUTTER_DEFAULTS };
}

const ButterContext = createContext<ButterContextType | undefined>(undefined);

export function ButterProvider({ children }: { children: ReactNode }) {
  const [settings, setSettings] = useState<ButterSettings>(loadFromStorage);

  const save = (next: ButterSettings) => {
    setSettings(next);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch {
      // ignore storage errors
    }
  };

  // Apply CSS custom properties + data attributes to <html>
  useEffect(() => {
    const root = document.documentElement;

    root.setAttribute('data-bt-style', settings.style);
    root.setAttribute('data-bt-hue', settings.hue);

    // --bt-blur: override the style-mode default with user-picked value
    root.style.setProperty('--bt-blur', `${settings.blur}px`);

    // Accent
    root.style.setProperty('--bt-accent', settings.accent);

    const rgb = hexToRgb(settings.accent);
    if (rgb) {
      const { r, g, b } = rgb;
      root.style.setProperty('--bt-accent-soft', `rgba(${r},${g},${b}, 0.18)`);
      root.style.setProperty('--bt-card-tinted', `rgba(${r},${g},${b}, 0.12)`);
    }
  }, [settings]);

  return (
    <ButterContext.Provider
      value={{
        settings,
        setStyle: (style) => save({ ...settings, style }),
        setHue: (hue) => save({ ...settings, hue }),
        setBlur: (blur) => save({ ...settings, blur }),
        setAccent: (accent) => save({ ...settings, accent }),
      }}
    >
      {children}
    </ButterContext.Provider>
  );
}

export function useButter() {
  const ctx = useContext(ButterContext);
  if (!ctx) throw new Error('useButter must be used within ButterProvider');
  return ctx;
}
